import type { Dispatch, SetStateAction } from 'react';
import { useEffect } from 'react';
import type { ReclamationType } from 'graphql-ts-types';

import { EventBusEvent } from 'common/enums/EventBus';
import type { Locale } from 'common/enums/Locale';
import { EditReclamationStep, ReclamationStep } from 'common/enums/Reclamation';
import { useGQLLocale } from 'common/hooks/locale';
import { useSubscription } from 'common/hooks/subscription';
import { useLocalization } from 'common/providers/LocaleProvider';
import type {
  ReclamationRecapItem,
  ReclamationUploadedPicturesChangeState,
} from 'common/types/reclamation';
import { hideAlert, showAlert } from 'common/utils/alert';
import { getTrackingEventType } from 'common/utils/reclamation/tracking';
import { updateReclamation } from '../../../reclamation-drawer/hooks/update-reclamation';
import { getUpdatedItems, openDrawer } from './helpers';

type SetIsLoading = Dispatch<SetStateAction<boolean>>;

type SetItems = Dispatch<SetStateAction<ReclamationRecapItem[]>>;

interface UpdateReclamationRecapItemOptions {
  reclamationId: string;
  errorMessage: string;
  setItems: SetItems;
  setIsLoading: SetIsLoading;
}

interface DeleteReclamationRecapItemOptions extends UpdateReclamationRecapItemOptions {
  confirmMessage: string;
}

interface UseAttributesEditOptions {
  reclamationId: string;
  reclamationType: ReclamationType;
  locale: Locale;
  items: ReclamationRecapItem[];
  setItems: SetItems;
  setIsLoading: SetIsLoading;
  isExternalMarketplace?: boolean;
}

interface UseActionHandlersOptions {
  reclamationId: string;
  reclamationType: ReclamationType;
  items: ReclamationRecapItem[];
  errorMessage: string;
  setIsLoading: SetIsLoading;
  setItems: SetItems;
  isExternalMarketplace?: boolean;
}

export const useHandleItemDelete = ({
  reclamationId,
  confirmMessage,
  errorMessage,
  setItems,
  setIsLoading,
}: DeleteReclamationRecapItemOptions) => {
  const locale = useGQLLocale();

  return async (itemId: string) => {
    const shouldDelete = window.confirm(confirmMessage);
    if (!shouldDelete) {
      return;
    }

    setIsLoading(true);

    try {
      await updateReclamation({
        locale,
        reclamationId,
        updateBatches: {
          id: itemId,
          isAddedToClaim: false,
        },
      });

      setItems(items => items.filter(({ id }) => id !== itemId));
      hideAlert();
    } catch {
      showAlert(errorMessage);
    }

    setIsLoading(false);
  };
};

const getTrackingDataTitle = (type: ReclamationStep): EditReclamationStep => {
  switch (type) {
    case ReclamationStep.Reasons:
      return EditReclamationStep.EditReasons;
    case ReclamationStep.Solutions:
      return EditReclamationStep.EditSolutions;
    default:
      return EditReclamationStep.EditAdditionalInfo;
  }
};

const useAttributesEdit = ({
  reclamationId,
  reclamationType,
  locale,
  items,
  isExternalMarketplace = false,
  setItems,
  setIsLoading,
}: UseAttributesEditOptions) =>
  [ReclamationStep.Reasons, ReclamationStep.Solutions, ReclamationStep.AdditionalInfo].map(
    type => (itemId: string) => {
      const trackingEventType = getTrackingEventType(reclamationType, items.length);
      const trackingEventData = {
        title: getTrackingDataTitle(type),
        ...(trackingEventType && { type: trackingEventType }),
      };
      const item = items.find(({ id }) => id === itemId);

      openDrawer({
        steps: [
          {
            type,
            reclamationId,
            itemId,
            article: item?.article ?? null,
            alert: null,
            trackingEventData,
            quantity: item?.quantity ?? 0,
            claimedQuantity: item?.claimedQuantity ?? 0,
          },
        ],
        reclamationId,
        locale,
        trackingEventData,
        isExternalMarketplace,
        setItems,
        setIsLoading,
      });
    }
  );

export const useActionHandlers = ({
  reclamationId,
  reclamationType,
  items,
  errorMessage,
  isExternalMarketplace,
  setIsLoading,
  setItems,
}: UseActionHandlersOptions) => {
  const { t, locale } = useLocalization();

  const [handleReasonEdit, handleSolutionEdit, handleAdditionalInfoEdit] = useAttributesEdit({
    reclamationId,
    reclamationType,
    locale,
    items,
    isExternalMarketplace,
    setItems,
    setIsLoading,
  });
  const handleItemDelete = useHandleItemDelete({
    reclamationId,
    confirmMessage: t('h24_delete_item_confirmation'),
    errorMessage,
    setItems,
    setIsLoading,
  });

  return {
    onReasonEdit: handleReasonEdit,
    onSolutionEdit: handleSolutionEdit,
    onAdditionalInfoEdit: handleAdditionalInfoEdit,
    onItemDelete: handleItemDelete,
  };
};

export const useUploadedPicturesStateChange = (setItems: SetItems) => {
  const [state] = useSubscription<ReclamationUploadedPicturesChangeState>(
    EventBusEvent.ReclamationUploadedPicturesStateChange
  );

  useEffect(() => {
    if (state) {
      const { itemId, pictures } = state;
      setItems(items => getUpdatedItems({ items, itemId, resources: pictures }));
    }
  }, [state, setItems]);
};
